/**
 * Chrome partagé des panneaux de canal (Telegram / Twitter), aligné sur le
 * détail créateur (`CreatorDetailView`) :
 *
 *   - `ChannelCreatorHeader` : en-tête « en haut » = photo + nom du créateur en
 *     DROPDOWN (pour changer de créateur) + 3 stats (messages / ventes /
 *     revenu), comme le header du détail créateur.
 *   - `ChannelNavRail` : rail de navigation VERTICAL à gauche (sous-pages du
 *     canal), repris tel quel du détail créateur (icon-only en panneau étroit
 *     via `@container`, barre d'accent `bg-mark` sur l'actif).
 *   - `ChannelEmptyPrompt` : invite centrée tant qu'aucun créateur n'est choisi.
 *
 * Plus de grille de cards de sélection : dans un canal on choisit le créateur
 * via le dropdown de l'en-tête (la grille de cards reste réservée au panneau
 * Créateurs).
 *
 * Zone commune des canaux (cf `channels/ARCHITECTURE.md`) : ne dépend ni de
 * `telegram/` ni de `twitter/`. L'import de `Creator` est type-only (aucun
 * cycle runtime).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { ChevronDown, type LucideIcon } from "@/shared/ui/icons";
import { Avatar, Button, Carte, Grille, Infobulle, Ligne, Lignes, Onglets, Saisie, StatTile, Vide, Volet, cn, contrastFg } from "@/shared/ui";
import type { Creator } from "@/panels/creators";

/* ============================================================ */
/* En-tête : photo + nom (dropdown switcher) + stats             */
/* ============================================================ */

export function ChannelCreatorHeader({
  creators,
  currentSlug,
  onSwitch,
  showStats = true,
}: {
  creators: Creator[];
  currentSlug: string;
  onSwitch: (slug: string) => void;
  /** Affiche la ligne de stats créateur (messages/ventes/revenu). Ces stats sont
   *  propres à Telegram (messages vendus en DM) → on les masque sur Twitter. */
  showStats?: boolean;
}) {
  const creator = creators.find((c) => c.slug === currentSlug) ?? null;
  return (
    // `relative z-20` : hisse l'en-tête (et son volet `absolute z-50`) au-dessus du corps (rail +
    // contenu), sinon le rail de nav se peint par-dessus le volet. Une hairline dérivée de l'encre
    // dessous, pas de fond : le verre du panneau passe.
    <div className="relative z-20 shrink-0 border-b border-fg/[0.07] px-4 py-3">
      <div className="flex flex-col gap-3">
        <CreatorDropdown creators={creators} creator={creator} onSwitch={onSwitch} />
        {showStats && creator && <StatsRow creator={creator} />}
      </div>
    </div>
  );
}

function StatsRow({ creator }: { creator: Creator }) {
  const { t } = useTranslation();
  return (
    <Grille min={120} ecart={8}>
      <StatTile
        taille="sm"
        tone="neutre"
        label={t("panels.creators.detail.messages")}
        value={formatCount(creator.messages_count)}
      />
      <StatTile taille="sm" tone="neutre" label={t("panels.creators.detail.sales")} value={formatCount(creator.sales_count)} />
      <StatTile
        taille="sm"
        tone="neutre"
        label={t("panels.creators.detail.revenue")}
        value={formatCents(creator.revenue_cents)}
      />
    </Grille>
  );
}

/* ── Sélecteur : trigger = avatar + nom + chevron ; volet = recherche + avatars + noms ── */

/**
 * « manque : Volet » — un popover à contenu libre (ici : un champ de recherche au-dessus d'une
 * liste d'avatars). `Menu` ne porte que des actions. En attendant, le volet est une `Carte` posée
 * sous le déclencheur (`bg-canvas`, opaque : elle passe au-dessus d'un rail et de contenus), avec un
 * `Champ` pilule et des `Lignes`.
 */
function CreatorDropdown({
  creators,
  creator,
  onSwitch,
}: {
  creators: Creator[];
  creator: Creator | null;
  onSwitch: (slug: string) => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  const filtered = useMemo(
    () => Saisie.filtrerParRecherche(creators, query, (c) => [c.name]),
    [creators, query],
  );

  return (
    <div ref={wrapperRef} className="relative">
      <Infobulle texte={t("panels.channels.switchCreator")} cote="bas">
        <Carte
          onClick={() => setOpen((v) => !v)}
          remplissage="sm"
          aria-haspopup="dialog"
          aria-expanded={open}
          className="flex items-center gap-3"
        >
          <CreatorAvatar creator={creator} taille={40} />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-semibold text-fg">
              {creator ? creator.name : t("panels.channels.switchCreator")}
            </span>
            <span className="ch-etiquette block truncate">{t("panels.channels.switchCreator")}</span>
          </span>
          <ChevronDown className="h-4 w-4 shrink-0 text-faint" />
        </Carte>
      </Infobulle>

      {/* Le `Volet` de la librairie : sous le sélecteur (au-dessus si la place manque), Échap et un
          clic dehors le ferment. */}
      <Volet
        ancre={wrapperRef}
        ouvert={open}
        onClose={() => setOpen(false)}
        ariaLabel={t("panels.channels.pickerTitle")}
        largeur={320}
      >
        <div className="flex flex-col gap-2">
          <Saisie.Recherche
            taille="sm"
            autoFocus
            valeur={query}
            onChange={setQuery}
            placeholder={t("panels.channels.searchPlaceholder", { count: creators.length })}
            ariaLabel={t("panels.channels.searchPlaceholder", { count: creators.length })}
          />
          <div className="max-h-80 overflow-y-auto">
            {filtered.length === 0 ? (
              <Vide taille="sm" titre={t("panels.channels.noMatch")} />
            ) : (
              <Lignes>
                {filtered.map((c) => (
                  <Ligne
                    key={c.id}
                    avant={<CreatorAvatar creator={c} taille={28} />}
                    etiquette={c.name}
                    actif={creator?.slug === c.slug}
                    onClick={() => {
                      onSwitch(c.slug);
                      setOpen(false);
                    }}
                  />
                ))}
              </Lignes>
            )}
          </div>
        </div>
      </Volet>
    </div>
  );
}

/** La tête d'un créateur : photo si dispo, sinon son initiale sur SA couleur (l'`Avatar` de la
 *  librairie ; la couleur est une donnée de la créatrice, comme celle d'un tag) ; un « ? » neutre
 *  si aucun créateur n'est sélectionné. */
function CreatorAvatar({ creator, taille }: { creator: Creator | null; taille: number }) {
  return (
    <Avatar
      src={creator?.avatar_url}
      nom={creator?.name ?? "?"}
      taille={taille}
      style={creator?.color ? { background: creator.color, color: contrastFg(creator.color) } : undefined}
    />
  );
}

/* ============================================================ */
/* Rail de navigation (repris du détail créateur)                 */
/* ============================================================ */

export interface ChannelNavItem {
  id: string;
  icon: LucideIcon;
  label: string;
}

/**
 * La navigation d'un canal : en HORIZONTAL (le canal X), de vrais `Onglets` ; en VERTICAL (Telegram,
 * Herald, le lab), une colonne de boutons — l'actif en verre avec le trait de la marque.
 * « manque : Onglets vertical » — utile ici ; `Onglets` ne sait faire que des rangées.
 */
export function ChannelNavRail({
  items,
  activeId,
  onSelect,
  ariaLabel,
  orientation = "vertical",
  className,
}: {
  items: ChannelNavItem[];
  activeId: string;
  onSelect: (id: string) => void;
  ariaLabel: string;
  /** Classes en plus sur le `<nav>` — sert à le poser DANS une rangée qui
   *  porte déjà sa bordure (`border-b-0`), quand un bouton partage sa ligne. */
  className?: string;
  /** `horizontal` : une barre d'onglets EN HAUT au lieu du rail à gauche.
   *
   *  Demandé par Kayla le 24-08-2026 pour le canal X — « qu'elle soit en haut,
   *  qu'elle soit horizontale plutôt qu'à gauche, verticale ». Le défaut reste
   *  `vertical` : Telegram, Herald et le lab gardent leur rail, on ne change
   *  pas trois panneaux pour une demande qui en visait un.
   *
   *  ⚠️ En horizontal, le libellé ne se cache PAS sous `@md` : une barre
   *  d'onglets réduite à des icônes ne se lit pas, là où un rail vertical
   *  garde au moins l'ordre vertical comme repère. */
  orientation?: "vertical" | "horizontal";
}) {
  if (orientation === "horizontal") {
    return (
      // Sa hairline est celle du <nav> (que l'appelant peut retirer, `border-b-0`) : celle des
      // `Onglets` est tue.
      <div className={cn("shrink-0 border-b border-fg/[0.07] px-2", className)}>
        <Onglets
          style="ligne"
          taille="md"
          nom={ariaLabel}
          options={items.map((item) => {
            const Icon = item.icon;
            return { valeur: item.id, etiquette: item.label, icone: <Icon className="h-4 w-4" /> };
          })}
          valeur={activeId}
          onChange={onSelect}
          className="[&_.og-ligne]:border-b-0"
        />
      </div>
    );
  }
  return (
    <nav
      role="tablist"
      aria-label={ariaLabel}
      aria-orientation="vertical"
      className={cn(
        "scrollbar-none flex w-12 shrink-0 flex-col gap-1 overflow-y-auto border-r border-fg/[0.07] p-2 @md:w-44",
        className,
      )}
    >
      {items.map((item) => {
        const isActive = item.id === activeId;
        const Icon = item.icon;
        return (
          <div key={item.id} className="relative shrink-0">
            {isActive && <span aria-hidden className="og-trait absolute -left-2 bottom-2 top-2 w-0.5" />}
            <Infobulle texte={item.label} cote="droite">
              <Button
                role="tab"
                variant={isActive ? "secondary" : "ghost"}
                size="lg"
                aria-selected={isActive}
                aria-label={item.label}
                onClick={() => onSelect(item.id)}
                className={cn("w-full px-0 @md:justify-start @md:px-4", isActive && "text-fg")}
                icon={<Icon className="h-4 w-4 shrink-0" />}
              >
                <span className="hidden truncate @md:inline">{item.label}</span>
              </Button>
            </Infobulle>
          </div>
        );
      })}
    </nav>
  );
}

/* ============================================================ */
/* Invite (aucun créateur choisi)                                 */
/* ============================================================ */

export function ChannelEmptyPrompt({ prompt }: { prompt: string }) {
  return (
    <div className="flex min-h-0 flex-1 items-center justify-center p-6">
      <Vide titre={prompt} />
    </div>
  );
}

/* ── helpers stats (repris de CreatorDetailView) ── */

function formatCount(n: number): string {
  if (!Number.isFinite(n)) return "—";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}

function formatCents(cents: number): string {
  if (!Number.isFinite(cents)) return "—";
  const dollars = cents / 100;
  return `$${dollars.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}
